import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect, useMouseTilt } from '../hooks/useIntersect';
import { VideoBackground, VideoReel, HoverVideoCard } from '../components/VideoComponents';

/* ── Animated headline word reveal ──────────────────────── */
function AnimatedHeadline() {
  const WORDS = ['Technology.', 'Innovation.', 'Education.', 'Impact.'];
  const [idx, setIdx]   = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setIdx(i => (i + 1) % WORDS.length); setShow(true); }, 350);
    }, 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="display-xl" style={{ color: 'var(--t-primary)', marginBottom: 28 }}>
      <div>Building Our</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <span>Future Through</span>
        <span style={{
          color: 'var(--gold)', display: 'inline-block',
          transition: 'opacity 0.3s, transform 0.3s',
          opacity: show ? 1 : 0,
          transform: show ? 'translateY(0)' : 'translateY(12px)',
          minWidth: '320px',
        }}>{WORDS[idx]}</span>
      </div>
    </div>
  );
}

/* ── Pillar card with 3D tilt ────────────────────────────── */
function PillarCard({ pillar, delay }) {
  const [ref, tilt] = useMouseTilt(8);
  const [intRef, visible] = useIntersect();
  const setRef = el => { ref.current = el; intRef.current = el; };

  return (
    <div ref={setRef} className="card" style={{
      padding: '32px 28px', cursor: 'default',
      transform: visible
        ? `perspective(600px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`
        : 'translateY(30px)',
      opacity: visible ? 1 : 0,
      transition: visible
        ? `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`
        : 'none',
      willChange: 'transform',
      background: 'linear-gradient(145deg, var(--bg-card) 0%, var(--bg-2) 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -30, right: -30, width: 100, height: 100,
        background: `radial-gradient(circle, ${pillar.glow}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: `${pillar.glow}18`, border: `1px solid ${pillar.glow}44`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, marginBottom: 20, color: pillar.glow,
      }}>{pillar.icon}</div>
      <div className="display-sm" style={{ marginBottom: 10, fontSize: '1.1rem' }}>{pillar.title}</div>
      <p className="body-sm" style={{ color: 'var(--t-secondary)', lineHeight: 1.65 }}>{pillar.desc}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20, color: pillar.glow, fontSize: '0.8rem', fontWeight: 600 }}>
        <Link to={pillar.link} style={{ color: 'inherit' }}>Explore →</Link>
      </div>
    </div>
  );
}

/* ── Marquee strip ───────────────────────────────────────── */
function Marquee() {
  const ITEMS = ['Web Development','UI/UX Design','Mobile Apps','Data Analytics','Digital Marketing','ICT Training','Media Production','Innovations','Youth Programs','Partnerships','Rwanda','Africa'];
  const doubled = [...ITEMS, ...ITEMS];
  return (
    <div style={{ overflow: 'hidden', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', padding: '14px 0' }}>
      <div style={{ display: 'flex', gap: 0, animation: 'marquee 30s linear infinite', width: 'max-content' }}>
        {doubled.map((item, i) => (
          <span key={i} style={{ padding: '0 28px', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: i % 4 === 1 ? 'var(--gold)' : 'var(--t-muted)', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

/* ── Stats row ───────────────────────────────────────────── */
function StatRow() {
  const [ref, visible] = useIntersect();
  const STATS = [
    { value: '5',    label: 'Core Pillars',       suffix: '' },
    { value: '50',   label: 'Programs & Courses', suffix: '+' },
    { value: '3',    label: 'Sub-Brands',         suffix: '' },
    { value: '1000', label: 'Community Members',  suffix: '+' },
    { value: '2024', label: 'Founded',            suffix: '' },
  ];
  return (
    <div ref={ref} style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, background: 'var(--border)' }}>
      {STATS.map((s, i) => (
        <div key={s.label} style={{ background: 'var(--bg-1)', padding: '36px 28px', textAlign: 'center', opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)', transition: `opacity 0.6s ${i * 0.1}s, transform 0.6s ${i * 0.1}s` }}>
          <div className="display-md" style={{ color: 'var(--gold)', marginBottom: 6 }}>{s.value}{s.suffix}</div>
          <div className="label" style={{ color: 'var(--t-muted)' }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Showreel section ────────────────────────────────────── */
function ShowreelSection() {
  const [ref, visible] = useIntersect();
  return (
    <section ref={ref} style={{
      padding: 'var(--section) 0',
      background: 'var(--bg-0)',
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(30px)',
      transition: 'opacity 0.7s, transform 0.7s',
    }}>
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          {/* Text side */}
          <div>
            <div className="section-label label" style={{ marginBottom: 20 }}>Who We Are</div>
            <h2 className="display-lg" style={{ marginBottom: 20 }}>
              See the Ecosystem<br />
              <span style={{ color: 'var(--gold)' }}>in Action</span>
            </h2>
            <p className="body-lg" style={{ color: 'var(--t-secondary)', marginBottom: 28, lineHeight: 1.75 }}>
              From training rooms in Gisenyi to digital platforms serving the continent — watch how MilleHills is building Africa's technology future, one initiative at a time.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/about" className="btn btn-primary">Our Story →</Link>
              <Link to="/programs" className="btn btn-outline">View Programs</Link>
            </div>
          </div>

          {/* Video reel side */}
          <div>
            <VideoReel
              slot="home_showreel"
              title="MilleHills Ecosystem Reel"
              label="Watch our story"
              aspectRatio="16/9"
              borderRadius={16}
            />
            {/* Fallback placeholder when no video is set */}
            <noscript>
              <div style={{ aspectRatio: '16/9', background: 'var(--bg-2)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--t-muted)', fontSize: '0.875rem' }}>Video coming soon</span>
              </div>
            </noscript>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Pillars ─────────────────────────────────────────────── */
const PILLARS = [
  { icon: '◐', title: 'Education & Training',  desc: "Hands-on bootcamps, workshops, and certification programs shaping Rwanda's next generation of technologists.",   glow: '#4e9cf5', link: '/programs'   },
  { icon: '◉', title: 'Services & Consulting', desc: 'End-to-end digital transformation: web, mobile, design, ICT infrastructure, and strategic technology consulting.', glow: '#e8a43a', link: '/services'   },
  { icon: '◈', title: 'Products & Platforms',  desc: "Proprietary platforms and digital products built for Africa's unique context and global ambition.",                 glow: '#3ecf8e', link: '/about'      },
  { icon: '◎', title: 'Media & Communication', desc: 'MARA Matsiko powers storytelling, digital media, and communications for the ecosystem and its communities.',       glow: '#f0a83a', link: '/about#mara' },
  { icon: '⬡', title: 'Innovation & Research', desc: "Applied research, rapid prototyping, and experimental initiatives pushing the boundaries of what's possible.",     glow: '#a78bfa', link: '/about'      },
];

/* ── HomePage ────────────────────────────────────────────── */
export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [ctaRef, ctaVisible] = useIntersect();

  useEffect(() => {
    publicAPI.articles({ limit: 3 }).then(r => setArticles(r.data?.data || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* ── HERO with video background ─────────────────── */}
      <VideoBackground
        slot="home_hero_video"
        overlayGrad="linear-gradient(135deg, rgba(8,10,13,0.75) 0%, rgba(8,10,13,0.4) 60%, rgba(232,164,58,0.08) 100%)"
        style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 'var(--nav-h)' }}
      >
        {/* Geometric rings (show on top of video) */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{ position: 'absolute', top: '10%', right: '-5%', width: 'clamp(300px,50vw,700px)', aspectRatio: '1', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%', animation: 'float 8s ease-in-out infinite' }} />
          <div style={{ position: 'absolute', top: '20%', right: '5%', width: 'clamp(180px,30vw,400px)', aspectRatio: '1', border: '1px solid rgba(232,164,58,0.1)', borderRadius: '50%', animation: 'float 10s ease-in-out infinite reverse' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 60% at 60% 40%, rgba(232,164,58,0.04) 0%, transparent 70%)' }} />
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-label label" style={{ marginBottom: 28, animation: 'fadeUp 0.7s ease both' }}>
            Technology & Innovation Ecosystem
          </div>
          <div style={{ animation: 'fadeUp 0.7s 0.1s ease both' }}>
            <AnimatedHeadline />
          </div>
          <p className="body-lg" style={{ color: '#fff', fontWeight: 400, maxWidth: 560, marginBottom: 40, animation: 'fadeUp 0.7s 0.2s ease both' }}>
            MilleHills Ltd is building a comprehensive technology ecosystem from Gisenyi, Rwanda —
            training talent, delivering services, and powering digital transformation across the continent.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', animation: 'fadeUp 0.7s 0.3s ease both' }}>
            <Link to="/services" className="btn btn-primary">Explore Services →</Link>
            <Link to="/programs" className="btn btn-outline">View Programs</Link>
          </div>
          <div style={{ marginTop: 60, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--t-muted)', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', animation: 'fadeUp 0.7s 0.5s ease both' }}>
            <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, var(--gold), transparent)' }} />
            Scroll to explore
          </div>
        </div>
      </VideoBackground>

      {/* ── MARQUEE ───────────────────────────────────────── */}
      <Marquee />

      {/* ── PILLARS ───────────────────────────────────────── */}
      <section style={{ padding: 'var(--section) 0', background: 'var(--bg-0)' }}>
        <div className="container">
          <div style={{ marginBottom: 56, maxWidth: 640 }}>
            <div className="section-label label">Five Pillars</div>
            <h2 className="display-lg" style={{ marginBottom: 16 }}>A Complete<br /><span style={{ color: 'var(--gold)' }}>Digital Ecosystem</span></h2>
            <p className="body-lg" style={{ color: 'var(--t-secondary)' }}>Every aspect of digital growth — from education to execution — under one ecosystem built for Africa.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {PILLARS.map((p, i) => <PillarCard key={p.title} pillar={p} delay={i * 0.08} />)}
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────── */}
      <StatRow />

      {/* ── SHOWREEL ──────────────────────────────────────── */}
      <ShowreelSection />

      {/* ── BRANDS STRIP ──────────────────────────────────── */}
      <section style={{ padding: 'var(--section) 0', background: 'var(--bg-1)' }}>
        <div className="container">
          <div className="section-label label" style={{ marginBottom: 48 }}>The Ecosystem</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              { name: 'MilleHills Core', tag: 'Parent Entity',   desc: 'The hub that anchors the entire ecosystem — strategy, governance, and operations.',                            color: 'var(--gold)',  icon: 'M' },
              { name: 'MARA Matsiko',    tag: 'Media Sub-Brand', desc: 'Digital media, storytelling, and communications powering the voice of the ecosystem.',                         color: 'var(--teal)',  icon: '◎' },
              { name: 'BayBees 2.1',    tag: 'Kids & Youth',    desc: "Creative, educational content and programs designed for Africa's youngest innovators.",                         color: '#f472b6',      icon: '◈' },
            ].map(b => (
              <div key={b.name} className="card" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 10, background: `${b.color}18`, border: `1px solid ${b.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: b.color }}>{b.icon}</div>
                  <div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem' }}>{b.name}</div>
                    <div className="label" style={{ color: 'var(--t-muted)', marginTop: 2 }}>{b.tag}</div>
                  </div>
                </div>
                <p className="body-sm" style={{ color: 'var(--t-secondary)' }}>{b.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/about" className="btn btn-outline">View Full Ecosystem →</Link>
          </div>
        </div>
      </section>

      {/* ── VIDEO FEATURE SECTION ─────────────────────────── */}
      <VideoBackground
        slot="home_feature_video"
        overlayGrad="linear-gradient(180deg, rgba(8,10,13,0.88) 0%, rgba(8,10,13,0.6) 50%, rgba(8,10,13,0.88) 100%)"
        style={{ padding: 'var(--section) 0' }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="section-label label" style={{ justifyContent: 'center', marginBottom: 20 }}>Our Work</div>
          <h2 className="display-lg" style={{ marginBottom: 16, maxWidth: 600, margin: '0 auto 16px' }}>
            Turning Vision into<br /><span style={{ color: 'var(--gold)' }}>Tangible Impact</span>
          </h2>
          <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.75 }}>
            From grassroots training to enterprise solutions — every project we take on leaves a lasting mark on the digital landscape of Africa.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/services" className="btn btn-primary">Our Services →</Link>
            <Link to="/contact"  className="btn btn-outline">Start a Project</Link>
          </div>
        </div>
      </VideoBackground>

      {/* ── LATEST NEWS ───────────────────────────────────── */}
      {articles.length > 0 && (
        <section style={{ padding: 'var(--section) 0', background: 'var(--bg-0)' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 40, flexWrap: 'wrap', gap: 16 }}>
              <div>
                <div className="section-label label">Latest</div>
                <h2 className="display-md">News & Insights</h2>
              </div>
              <Link to="/news" className="btn btn-ghost" style={{ color: 'var(--gold)' }}>View all →</Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              {articles.map((a, i) => (
                <Link key={a.id} to={`/news/${a.slug}`} className="card" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 12, opacity: 0, animation: `fadeUp 0.5s ${i * 0.1}s ease forwards` }}>
                  {/* Article cover image if available */}
                  {a.media_url && (
                    <div style={{ height: 140, borderRadius: 8, overflow: 'hidden', margin: '-28px -28px 0', marginBottom: 0 }}>
                      <img src={a.media_url} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  <div className="badge badge-dim">{a.content_type}</div>
                  <h3 className="display-sm" style={{ fontSize: '1.05rem', lineHeight: 1.3 }}>{a.title}</h3>
                  <p className="body-sm" style={{ color: 'var(--t-secondary)', flex: 1 }}>{a.excerpt}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--t-muted)' }}>{a.published_at ? new Date(a.published_at).toLocaleDateString() : ''}</span>
                    <span style={{ color: 'var(--gold)', fontSize: '0.85rem' }}>Read →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA BANNER ────────────────────────────────────── */}
      <section ref={ctaRef} style={{ padding: 'clamp(60px,8vw,100px) 0', background: 'linear-gradient(135deg, var(--bg-1) 0%, var(--bg-2) 100%)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(232,164,58,0.06) 0%, transparent 70%)' }} />
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1, opacity: ctaVisible ? 1 : 0, transform: ctaVisible ? 'none' : 'translateY(20px)', transition: 'opacity 0.7s, transform 0.7s' }}>
          <div className="section-label label" style={{ justifyContent: 'center', marginBottom: 20 }}>Start Today</div>
          <h2 className="display-lg" style={{ marginBottom: 20 }}>Ready to be part of<br /><span style={{ color: 'var(--gold)' }}>the ecosystem?</span></h2>
          <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 500, margin: '0 auto 36px' }}>
            Whether you're a student, a business, a partner, or a believer — there's a place for you in MilleHills.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact?type=client"    className="btn btn-primary">Work With Us</Link>
            <Link to="/contact?type=volunteer" className="btn btn-outline">Get Involved</Link>
            <Link to="/programs"               className="btn btn-ghost" style={{ color: 'var(--gold)' }}>Browse Programs →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}